import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Share2, Copy, Check } from 'lucide-react';
import { SOCIAL_CARD } from '../utils/brand';
import TipForm from '../components/TipForm';
import TopNav from '../components/sections/TopNav';
import PageSeoTag from '../components/PageSeoTag';

const PAGE_URL = (() => {
  try {
    return `${window.location.origin}/tip`;
  } catch {
    return 'https://outlawmedia.com.au/tip';
  }
})();

const META = {
  title: "Got a Tip? · Outlaw Media",
  description:
    "Anonymous tip line for Outlaw Media. Drop names, locations, evidence - no IP tracking, no device fingerprinting. We protect our sources.",
  image: SOCIAL_CARD,
};

/**
 * Sets head meta tags for social-share previews while this page is mounted.
 * Restores prior values on unmount so other routes aren't affected.
 */
const useSocialMeta = () => {
  useEffect(() => {
    const prev = { title: document.title };
    document.title = META.title;

    const setMeta = (name, content, attr = 'name') => {
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      const previous = tag.getAttribute('content');
      tag.setAttribute('content', content);
      return { tag, previous, created };
    };

    const ledger = [
      setMeta('description', META.description),
      setMeta('og:title', META.title, 'property'),
      setMeta('og:description', META.description, 'property'),
      setMeta('og:image', META.image, 'property'),
      setMeta('og:url', PAGE_URL, 'property'),
      setMeta('og:type', 'website', 'property'),
      setMeta('twitter:card', 'summary_large_image'),
      setMeta('twitter:title', META.title),
      setMeta('twitter:description', META.description),
      setMeta('twitter:image', META.image),
    ];

    return () => {
      document.title = prev.title;
      ledger.forEach(({ tag, previous, created }) => {
        if (created) tag.parentNode?.removeChild(tag);
        else if (previous !== null) tag.setAttribute('content', previous);
      });
    };
  }, []);
};

const SubmitTip = () => {
  useSocialMeta();
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareData = {
      title: META.title,
      text: META.description,
      url: PAGE_URL,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(PAGE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="submit-tip-page">
      <TopNav />
      <PageSeoTag pageKey="tip" />

      {/* Hero band */}
      <div className="relative pt-8 pb-6 px-4 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto">
          <Link
            to="/"
            data-testid="submit-tip-back-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-red-400 transition-colors mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Home
          </Link>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-1">
                Tip Line
              </p>
              <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-tight">
                Got Something <span className="text-red-500">We Should Know?</span>
              </h1>
            </div>

            <button
              type="button"
              onClick={handleShare}
              data-testid="submit-tip-share-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-700 hover:border-red-500/50 text-sm font-bold uppercase tracking-wider transition-colors"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span className="text-emerald-300">Link Copied</span>
                </>
              ) : (
                <>
                  <Share2 className="h-4 w-4 text-red-400" />
                  Share
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Form */}
      <main className="px-4 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto bg-zinc-950/80 border border-red-900/40 rounded-2xl p-5 sm:p-8 shadow-[0_0_40px_rgba(220,38,38,0.08)]">
          <TipForm />
        </div>

        <p className="max-w-2xl mx-auto text-center text-[11px] text-gray-600 uppercase tracking-widest mt-6">
          Direct URL · {PAGE_URL.replace(/^https?:\/\//, '')}
        </p>
      </main>
    </div>
  );
};

export default SubmitTip;
